// plugins/fetchSiteInfo.js
import { useMainStore } from "~/composables/store"; // 引入 Pinia Store
import { getCurrentServer } from "../utils/globalData";
import { resolveSiteTemplate } from "@/templates/_core/getCurrentTemplate";
import config from "@/config/index";
import {
  LOCALE_COOKIE_KEY,
  LOCALE_PREFERENCE_MODE_KEY,
  LOCALE_PREFERENCE_MODES,
  LOCALE_TEMPLATE_KEY,
} from "@/utils/localePreference";
import { traceSsr } from "@/utils/ssrTrace";
import { sendRedirect } from "h3";
import { resolveShopStyle } from "@/utils/shopStyle";

// 检查域名重定向
function checkDomainRedirect(siteInfo, nuxtApp) {
  try {
    const configData = siteInfo?.configData;
    if (!configData) {
      return false;
    }

    let domainTree = [];
    if (typeof configData === 'string') {
      try {
        const parsedConfigData = JSON.parse(configData);
        domainTree = parsedConfigData?.domainTree || [];
      } catch (e) {
        return false;
      }
    } else {
      domainTree = configData?.domainTree || [];
    }

    if (!domainTree || domainTree.length === 0) {
      return false;
    }

    // 获取当前域名
    let currentHost = '';
    let currentPath = '/';
    
    if (process.server) {
      const headers = useRequestHeaders();
      currentHost = headers.host || '';
      // 服务端获取当前路径
      const url = nuxtApp.ssrContext?.event?.node?.req?.url || '/';
      currentPath = url;
    } else if (process.client) {
      currentHost = window.location.host;
      currentPath = window.location.pathname + window.location.search + window.location.hash;
    }

    if (!currentHost) {
      return false;
    }

    for (const mainDomainNode of domainTree) {
      const mainDomain = mainDomainNode.domain;
      const children = mainDomainNode.children || [];

      let mainDomainHost = '';
      try {
        const mainUrl = new URL(mainDomain);
        mainDomainHost = mainUrl.host;
      } catch (e) {
        mainDomainHost = mainDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
      }

      // 如果当前域名就是主域名，不需要重定向
      if (currentHost === mainDomainHost) continue;

      for (const subDomain of children) {
        const subDomainUrl = subDomain.domain;
        const subDomainType = subDomain.type;

        let subDomainHost = '';
        try {
          const url = new URL(subDomainUrl);
          subDomainHost = url.host;
        } catch (e) {
          subDomainHost = subDomainUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
        }

        if (currentHost === subDomainHost && subDomainType === 'redirect') {
          const normalizedMainDomain = mainDomain.endsWith('/') ? mainDomain : `${mainDomain}/`;
          const targetUrlObj = new URL(currentPath || '/', normalizedMainDomain);
          targetUrlObj.searchParams.set('redirectSubDomain', subDomainHost);
          const targetUrl = targetUrlObj.toString();

          if (process.server) {
            // 服务端：使用 sendRedirect
            const event = nuxtApp.ssrContext?.event;
            if (event) {
              // 使用 h3 的 sendRedirect
              return sendRedirect(event, targetUrl, 301);
            }
          } else if (process.client) {
            // 客户端：立即跳转
            window.location.replace(targetUrl);
            // 返回 true 表示需要重定向，阻止后续执行
            return true;
          }
        }
      }
    }
  } catch (error) {
    // 只打印真正的异常错误，不打印预期的重定向错误
    console.error('Error checking domain redirect:', error.message || error);
    return false;
  }
  return false;
}

async function applyTemplateDefaultLocale(store, currentServer, nuxtApp) {
  const localeCookie = useCookie(LOCALE_COOKIE_KEY);
  const localePreferenceModeCookie = useCookie(LOCALE_PREFERENCE_MODE_KEY);
  const localeTemplateKeyCookie = useCookie(LOCALE_TEMPLATE_KEY);

  const storeInfo = store.getStoreInfoDataMap(currentServer) || {};
  const storePageInfo = storeInfo?.storePageInfo || {};
  const siteTemplate = resolveSiteTemplate(store.getSiteInfo(), {
    ...storeInfo,
    ...storePageInfo,
  });
  const templateKey = String(siteTemplate?.templateKey || "").trim();
  const templateDefaultLocale = String(
    siteTemplate?.defaultLocale || config.defaultLocale || ""
  ).trim();
  const currentLocale = String(localeCookie.value || "").trim();
  const localePreferenceMode = String(
    localePreferenceModeCookie.value || ""
  ).trim();
  const localeTemplateKey = String(localeTemplateKeyCookie.value || "").trim();

  if (!templateDefaultLocale) {
    return;
  }

  const shouldPreserveManualLocale =
    localePreferenceMode === LOCALE_PREFERENCE_MODES.MANUAL && currentLocale;
  const shouldKeepAutoLocaleForSameTemplate =
    localePreferenceMode === LOCALE_PREFERENCE_MODES.AUTO &&
    currentLocale &&
    localeTemplateKey &&
    localeTemplateKey === templateKey;

  if (shouldPreserveManualLocale || shouldKeepAutoLocaleForSameTemplate) {
    return;
  }

  const i18n = nuxtApp?.$i18n;

  try {
    if (typeof i18n?.setLocale === "function") {
      await i18n.setLocale(templateDefaultLocale);
    } else if (i18n?.locale && typeof i18n.locale === "object" && "value" in i18n.locale) {
      i18n.locale.value = templateDefaultLocale;
    } else if (typeof i18n?.locale === "string") {
      i18n.locale = templateDefaultLocale;
    }

    if (typeof i18n?.setLocaleCookie === "function") {
      i18n.setLocaleCookie(templateDefaultLocale);
    }
  } catch (error) {
    console.error("Error applying template default locale:", error);
  }

  localeCookie.value = templateDefaultLocale;
  localePreferenceModeCookie.value = LOCALE_PREFERENCE_MODES.AUTO;
  localeTemplateKeyCookie.value = templateKey;
}

export default defineNuxtPlugin(async (nuxtApp) => {
  const store = useMainStore();
  const headers = useRequestHeaders();
  const requestFetch = useRequestFetch();
 
  const currentServer = getCurrentServer(headers);

  traceSsr("fetchSiteInfoPlugin.start", {
    currentServer,
    host: headers?.host || "",
    xCustomHeader1: headers?.["x-custom-header1"] || "",
    xForwardedHost: headers?.["x-forwarded-host"] || "",
    xForwardedProto: headers?.["x-forwarded-proto"] || "",
    requestUrl: nuxtApp?.ssrContext?.event?.node?.req?.url || "",
  });

  if (process.client) {
    store.initCurrentCurrency();
  }

  // 在插件加载时调用 fetchSiteInfo 方法
  await store.fetchSiteInfo(currentServer);
  
  // 获取 siteInfo 后立即检查域名重定向（最早时机）
  const siteInfo = store.getSiteInfo();
  const needRedirect = checkDomainRedirect(siteInfo, nuxtApp);
  
  // 如果需要重定向，停止后续加载
  if (needRedirect) {
    return;
  }
  
  await store.fetchStoreInfo(currentServer);
  await applyTemplateDefaultLocale(store, currentServer, nuxtApp);
  await store.initCurrencyData({
    currentServer,
    $fetch: requestFetch,
    headers,
  });
  await store.fetchLayoutConfig(currentServer);
  await store.fetchSiteMenu(currentServer);

  const storeInfo = store.getStoreInfoDataMap(currentServer) || {};
  const storePageInfo = storeInfo?.storePageInfo || {};
  const styleInfo = resolveShopStyle({
    ...store.getSiteInfo(),
    ...storeInfo,
    ...storePageInfo,
  });
  const siteTemplate = resolveSiteTemplate(store.getSiteInfo(), {
    ...storeInfo,
    ...storePageInfo,
  });

  traceSsr("fetchSiteInfoPlugin.resolved", {
    currentServer,
    siteInfoId: store.getSiteInfo()?.id || "",
    siteInfoTitle: store.getSiteInfo()?.title || "",
    siteInfoShopStyle: store.getSiteInfo()?.shopStyle || "",
    storeInfoId: storeInfo?.id || "",
    storeInfoTitle: storeInfo?.title || "",
    storeInfoShopStyle: storeInfo?.shopStyle || "",
    storeInfoStoreStyle: storeInfo?.storeStyle || "",
    storeInfoThemeStyle: storeInfo?.themeStyle || "",
    storePageInfoId: storePageInfo?.id || "",
    styleRaw: styleInfo?.raw || "",
    styleNormalized: styleInfo?.normalized || "",
    templateKey: siteTemplate?.templateKey || "",
    templateStyle: siteTemplate?.style || siteTemplate?.templateStyle || "",
    defaultLocale: siteTemplate?.defaultLocale || "",
  });
});
